import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, User, Crown, ChevronDown } from 'lucide-react';
import { useMember, TIER_LABEL } from '../access/MemberAuthContext';
import '../styles/StrategicEnquiry.css';
import '../styles/MegaMenu.css';

/**
 * SiteHeader — logo + burger on every device. Burger: Home, About, then
 * collapsible Family / Organisations service groups, then Sign in.
 */
const GROUPS = [
  {
    id: 'family',
    label: 'Family',
    items: [
      { to: '/access', label: 'N3URO Access', hint: 'Membership, Profiles & Passport' },
      { to: '/experiences', label: 'Experiences', hint: 'Home experiences & celebrations' },
      { to: '/academy', label: 'Academy', hint: 'Flight & Rail pathways' },
      { to: '/n3uropedia', label: 'Knowledge · N3UROPEDIA', hint: 'Neurodiversity A-Z, travel & place access' },
    ],
  },
  {
    id: 'organisations',
    label: 'Organisations',
    items: [
      { to: '/advisory', label: 'N3URO Advisory', hint: 'Strategic advisory & reviews' },
      { to: '/developers-partners', label: 'Developers & Partners', hint: 'Partnerships & briefings' },
      { to: '/impact', label: 'Impact', hint: 'Outcomes & evidence' },
    ],
  },
];

const groupFor = (path) => GROUPS.find((g) => g.items.some((i) => path.startsWith(i.to)))?.id || null;

const SiteHeader = ({ currentPath = '/' }) => {
  const { member } = useMember();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => groupFor(currentPath));
  const wrapRef = useRef(null);
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => { setOpen(false); }, [currentPath]);

  return (
    <div ref={wrapRef}>
      <header className="strategic-header" data-testid="site-header">
        <div className="strategic-header-inner">
          <Link to="/" className="strategic-brand" data-testid="site-brand-home">
            <img width="600" height="180" loading="eager" decoding="async"
              src="/img/n3uro-world-logo-wide.webp"
              alt="N3URO WORLD®"
            />
          </Link>
          <button
            type="button"
            className="strategic-nav-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            data-testid="site-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {open && (
        <nav className="strategic-mobile-menu" aria-label="Site menu" data-testid="site-mobile-menu">
          <Link to="/" className="strategic-mobile-link" onClick={close} data-testid="site-mobile-root">Home</Link>
          <Link to="/about" className="strategic-mobile-link" onClick={close} data-testid="site-mobile-about">About</Link>
          <Link to="/what-is-n3uro-world" className="strategic-mobile-link" onClick={close} data-testid="site-mobile-what-is">What is N3URO WORLD®?</Link>

          {GROUPS.map((g) => {
            const isOpen = expanded === g.id;
            return (
              <div key={g.id} className={`mm-group-drop ${isOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="strategic-mobile-link mm-group-toggle"
                  aria-expanded={isOpen}
                  aria-controls={`mm-group-${g.id}`}
                  onClick={() => setExpanded(isOpen ? null : g.id)}
                  data-testid={`site-mobile-group-${g.id}`}
                >
                  <span>{g.label}</span>
                  <ChevronDown size={18} className="mm-group-chev" aria-hidden="true" />
                </button>
                {isOpen && (
                  <div className="mm-group-items" id={`mm-group-${g.id}`} data-testid={`site-mobile-group-${g.id}-items`}>
                    {g.items.map((l) => (
                      <Link key={l.to} to={l.to} className="strategic-mobile-link mm-service-link" onClick={close} data-testid={`site-mobile-${l.to.replace(/\//g, '')}`}>
                        <span className="mm-item-label">{l.label}</span>
                        <span className="mm-item-hint">{l.hint}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div className="mm-mob-divider" aria-hidden="true" />
          {member ? (
            <Link to="/access/account" className="strategic-mobile-link" onClick={close} data-testid="site-mobile-account">
              <Crown size={14} /> My Access · {TIER_LABEL[member.tier] || 'Free'}
            </Link>
          ) : (
            <Link to="/access/sign-in" className="strategic-mobile-link" onClick={close} data-testid="site-mobile-signin">
              <User size={14} /> Sign in
            </Link>
          )}
        </nav>
      )}
    </div>
  );
};

export default SiteHeader;
